import React from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';

import { BigButton } from '@components/Button';
import Divider from '@components/Divider';
import { GroupData } from '@components/Group/Card';
import { LargeGroupCardList } from '@components/Group/CardList';
import IconButton from '@components/IconButton';
import Placeholder, { EmptySearchPlaceholder } from '@components/Placeholders';
import ScreenContainer from '@components/ScreenContainer';
import SearchBar from '@components/SearchBar';
import { Route, TabView } from '@components/TabView';
import { PrimaryHeadline } from '@components/Typography';
import { LoadingView } from '@components/View';
import { useGroups } from '@context/GroupsContext';
import { useRecommendations } from '@context/RecommendationsContext';
import { useOnboardingStatus } from '@context/UsersContext';
import { useGroupSearch } from '@db/Search';
import { createAppNavigation } from '@navigation/AppNavigation';
import { useHeader } from '@navigation/Header';
import type { ViewGroupsScreenProps as NavigationViewGroupsScreenProps } from '@navigation/Types';

enum GroupTab {
  NEW = 'NEW',
  JOINED = 'JOINED',
}

type ViewGroupsScreenProps = {
  navigation: NavigationViewGroupsScreenProps['navigation'];
  route?: NavigationViewGroupsScreenProps['route'];
};

export default function ViewGroupsScreen({ navigation, route }: ViewGroupsScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  useHeader({ appNavigation, navigation });

  const { setOnboarded } = useOnboardingStatus();
  const { userGroups, userGroupIds } = useGroups();
  const { recommendedGroups } = useRecommendations();
  const [searchText, setSearchText] = React.useState<string>('');
  const [activeTab, setActiveTab] = React.useState<GroupTab>(GroupTab.NEW);
  const isOnboarding = route?.params?.screenType === 'onboarding';
  const activeSearchTab = isOnboarding ? GroupTab.NEW : activeTab;
  const joinedGroupIds = React.useMemo(() => [...userGroupIds], [userGroupIds]);
  const { loading, results: groupSearchResults } = useGroupSearch({
    query: searchText,
    includedGroupIds: activeSearchTab === GroupTab.JOINED ? joinedGroupIds : undefined,
    excludedGroupIds: activeSearchTab === GroupTab.NEW ? joinedGroupIds : undefined,
  });

  const groupsToJoin = React.useMemo(
    () => recommendedGroups.filter((group) => !userGroupIds.has(group.groupId)),
    [recommendedGroups, userGroupIds]
  );
  const newGroups = searchText ? groupSearchResults : groupsToJoin;
  const joinedGroups = searchText ? groupSearchResults : userGroups;

  const onPressGroup = (groupId: string) => {
    if (!isOnboarding) {
      appNavigation.openGroup(groupId);
    }
  };

  const renderEmptyComponent = ({ emptyTitle }: { emptyTitle: string }) => (
    <LoadingView loading={loading} indicatorSize="large">
      {searchText ? (
        <EmptySearchPlaceholder />
      ) : (
        <Placeholder
          title={emptyTitle}
          icon="account-multiple"
          testID="screens.ViewGroupsScreen.PlaceholderSurface.NoGroupsJoined"
        />
      )}
    </LoadingView>
  );

  const renderListHeaderComponent = () => (
    <View style={isOnboarding ? styles.onboardingSearchContainer : styles.searchContainer}>
      <SearchBar
        value={searchText}
        onChangeText={(text) => setSearchText(text)}
        loading={loading}
        placeholder="Search groups"
        style={styles.searchBar}
        testID="screens.ViewGroupsScreen.SearchBar"
      />
      {!isOnboarding ? (
        <IconButton
          icon="account-multiple-plus"
          onPress={appNavigation.openCreateGroup}
          testID="screens.ViewGroupsScreen.Button.CreateGroup"
          style={styles.createButton}
        />
      ) : null}
    </View>
  );

  const renderGroupList = (groups: GroupData[], emptyTitle: string) => (
    <LargeGroupCardList
      data={groups}
      itemProps={{ onPress: onPressGroup }}
      ListEmptyComponent={renderEmptyComponent({ emptyTitle })}
      style={styles.list}
    />
  );

  const renderScene = ({ route }: { route: Route }) => {
    switch (route.key) {
      case GroupTab.NEW:
        return renderGroupList(newGroups, 'No groups to join');
      case GroupTab.JOINED:
        return renderGroupList(joinedGroups, 'No groups joined');
      default:
        return null;
    }
  };

  const routes: Route[] = [
    { key: GroupTab.NEW, title: 'New' },
    { key: GroupTab.JOINED, title: 'Joined' },
  ];

  return (
    <ScreenContainer
      testID="screens.ViewGroupsScreen.Background"
      style={isOnboarding ? styles.onboardingBackground : styles.background}>
      {isOnboarding ? (
        <>
          <PrimaryHeadline style={styles.headline}>Join Groups</PrimaryHeadline>
          <Divider style={styles.divider} />
        </>
      ) : null}
      {renderListHeaderComponent()}
      {isOnboarding ? (
        renderGroupList(newGroups, 'No groups to join')
      ) : (
        <View style={styles.tabContainer}>
          <TabView
            routes={routes}
            renderScene={renderScene}
            onIndexChange={(index) => setActiveTab(routes[index].key as GroupTab)}
          />
        </View>
      )}
      {isOnboarding ? (
        <>
          <Divider style={styles.divider} />
          <View style={styles.buttonContainer}>
            <BigButton
              mode="contained"
              onPress={() => setOnboarded(true)}
              style={styles.button}
              testID="screens.JoinGroupsScreen.Button.Continue">
              Continue
            </BigButton>
          </View>
        </>
      ) : null}
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  background: {
    justifyContent: 'flex-start',
  },
  button: {
    alignSelf: 'center',
  },
  buttonContainer: {
    padding: 10,
    paddingHorizontal: 20,
    width: '100%',
  },
  createButton: {
    margin: 5,
  },
  divider: {
    width: '100%',
  },
  headline: {
    paddingVertical: 10,
    textAlign: 'center',
  },
  list: {
    flex: 1,
    width: '100%',
  },
  onboardingBackground: {
    maxHeight: Dimensions.get('window').height,
  },
  onboardingSearchContainer: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    margin: 5,
    marginVertical: 10,
  },
  searchBar: {
    flex: 1,
  },
  searchContainer: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    margin: 5,
    marginTop: 10,
  },
  tabContainer: {
    flex: 1,
    width: '100%',
  },
});
